import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subscription } from 'rxjs';
import { AuthService } from '../../service/auth.service';
import { HttpAgentSelfService } from '../../service/http-agent.service';
import { AgentDto } from '../../service/dto';

@Component({
    selector: 'app-force-password-change',
    standalone: false,
    templateUrl: './force-password-change.component.html',
    styleUrl: './force-password-change.component.scss'
})
export class ForcePasswordChangeComponent implements OnInit, OnDestroy {
    isVisible = false;
    isSubmitting = false;
    policyErrors: string[] = [];
    passwordForm!: FormGroup;

    passwordVisible = false;
    confirmPasswordVisible = false;

    private subscriptions: Subscription = new Subscription();

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        private agentSelfService: HttpAgentSelfService,
        private msg: NzMessageService
    ) { }

    ngOnInit(): void {
        this.passwordForm = this.fb.group({
            newPassword: ['', [Validators.required]],
            confirmPassword: ['', [Validators.required, this.confirmationValidator]]
        });

        this.subscriptions.add(
            this.authService.forcePasswordChange$.subscribe(state => {
                this.isVisible = state.required;
                this.policyErrors = state.errors || [];
                if (!this.isVisible) {
                    this.passwordForm.reset();
                    this.passwordVisible = false;
                    this.confirmPasswordVisible = false;
                }
            })
        );

        this.subscriptions.add(
            this.passwordForm.get('newPassword')?.valueChanges.subscribe(() => {
                this.validateConfirmPassword();
            })
        );
    }

    confirmationValidator: ValidatorFn = (control: AbstractControl): { [s: string]: boolean } => {
        if (!control.value) {
            return { required: true };
        } else if (control.value !== this.passwordForm?.controls['newPassword'].value) {
            return { mismatch: true, error: true };
        }
        return {};
    };

    validateConfirmPassword(): void {
        setTimeout(() => this.passwordForm.controls['confirmPassword'].updateValueAndValidity());
    }

    submitForm(): void {
        if (this.passwordForm.valid) {
            this.isSubmitting = true;
            const newPwd = this.passwordForm.get('newPassword')?.value;

            const currentAgent = this.authService.authState?.agent;

            if (!currentAgent) {
                this.msg.error('User context not found. Please log in again.');
                this.isSubmitting = false;
                return;
            }

            const payload = {
                id: currentAgent.id,
                username: currentAgent.username,
                password: newPwd
            } as AgentDto;

            this.agentSelfService.updatePassword(payload)
                .subscribe({
                    next: (agent) => {
                        this.isSubmitting = false;
                        if (agent.passwordPolicyErrors && agent.passwordPolicyErrors.length > 0) {
                            this.policyErrors = agent.passwordPolicyErrors;
                            this.msg.warning('Password does not meet policy requirements.');
                        } else {
                            this.msg.success('Password updated successfully');
                            this.authService.clearForcedPasswordState();
                        }
                    },
                    error: (err) => {
                        this.isSubmitting = false;
                        this.msg.error(err.error?.message || 'A network or server error occurred. Failed to update password.');
                    }
                });
        } else {
            Object.values(this.passwordForm.controls).forEach(control => {
                if (control.invalid) {
                    control.markAsDirty();
                    control.updateValueAndValidity({ onlySelf: true });
                }
            });
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
